import { Link } from "react-router-dom"
import { Address } from "viem"

import { Card } from "@/components/card"
import { CopyIcon } from "@/domains/icons/huge-icons"
import { trimEthAddress } from "@/domains/library"
import { MinimalPoolStore } from "@/domains/minimal-pool/minimal-pool-store"
import { useChainConfig } from "@/hooks/use-chain-config"
import useResponsive from "@/hooks/use-responsive"
import useScanner from "@/hooks/use-scanner"

export const MinimalPoolAddressesCard = ({ minimalPool }: { minimalPool: MinimalPoolStore | undefined | null }) => {
  if (!minimalPool) {
    return null
  }

  return (
    <Card.Container className="p-4">
      <TokenAddressInfo
        address={minimalPool.token0.address.toViemAddress()}
        label="Asset"
        symbol={minimalPool.token0.symbol ?? ""}
      />
      <TokenAddressInfo
        address={minimalPool.token1.address.toViemAddress()}
        label="Base"
        symbol={minimalPool.token1.symbol ?? ""}
      />
      <ClassicPoolAddressInfo address={minimalPool.address.toViemAddress()} label="Pool" />
      {minimalPool.hasGauge && minimalPool.gaugeAddress && (
        <GaugeAddressInfo address={minimalPool.gaugeAddress.toViemAddress()} label="Gauge" />
      )}
      {/* @todo: do we have the bribe address? */}
      <BribesAddressInfo address={minimalPool?.gauge?.bribeAddress?.toViemAddress()} label="Bribe" />
      {/* <FeesAddressInfo address={pair?.gauge?.feesAddress as Address} label="Fees" /> */}
    </Card.Container>
  )
}

export const TokenAddressInfo = ({
  address,
  label,
  symbol,
}: {
  address: Address | undefined
  label: string | undefined
  symbol: string | undefined
}) => {
  const scanner = useScanner()

  if (!address || !label || !symbol) {
    return null
  }

  const baseUrl = scanner.url
  const contractUrl = `${baseUrl}address/${address}#code`
  const holdersUrl = `${baseUrl}token/${address}#balances`
  const recentTransfersUrl = `${baseUrl}token/${address}`

  return (
    <div className="text-t100">
      <DisplayLabel label={`${label} - ${symbol}`} />
      <DisplayAddress address={address} />
      <div className="space-x-2 text-xs text-secondary-300">
        <a href={contractUrl} target="_blank" rel="noopener noreferrer">
          Contract
        </a>
        <ItemSeparator />
        <a href={holdersUrl} target="_blank" rel="noopener noreferrer">
          Holders
        </a>
        <ItemSeparator />
        <a href={recentTransfersUrl} target="_blank" rel="noopener noreferrer">
          Recent Transfers
        </a>
      </div>
    </div>
  )
}

export const ClassicPoolAddressInfo = ({
  address,
  label,
}: {
  address: Address | undefined
  label: string | undefined
}) => {
  const scanner = useScanner()

  if (!address || !label) {
    return null
  }

  const baseUrl = scanner.url
  const externalAddress = address.toLowerCase()

  return (
    <div className="text-t100">
      <DisplayLabel label={label} />
      <DisplayAddress address={address} />
      <div className="space-x-2 text-xs text-secondary-300">
        <ContractLink baseUrl={baseUrl} externalAddress={externalAddress} />
        <ItemSeparator />
        <DebankLink externalAddress={externalAddress} />
        <ItemSeparator />
        <DexScreenerChartLink externalAddress={externalAddress} />
        <ItemSeparator />
        <Link to={`/holders/${externalAddress}`} className="text-secondary-300">
          Holders
        </Link>
        <ItemSeparator />
        <Link to={`/actions/${externalAddress}`} className="text-secondary-300">
          Recent Txs
        </Link>
      </div>
    </div>
  )
}

export const GaugeAddressInfo = ({ address, label }: { address: Address | undefined; label: string | undefined }) => {
  const scanner = useScanner()

  if (!address || !label) {
    return null
  }

  const baseUrl = scanner.url
  const externalAddress = address.toLowerCase()
  const farmersListUrl = `${baseUrl}token/tokenholderchart/${externalAddress}`

  return (
    <div className="text-t100">
      <DisplayLabel label={label} />
      <DisplayAddress address={address} />
      <div className="space-x-2 text-xs text-secondary-300">
        <ContractLink baseUrl={baseUrl} externalAddress={externalAddress} />
        <ItemSeparator />
        <a href={farmersListUrl} target="_blank" rel="noopener noreferrer">
          View all farmers
        </a>
        <ItemSeparator />
        <DebankLink externalAddress={externalAddress} />
      </div>
    </div>
  )
}

export const FeesAddressInfo = ({ address, label }: { address: Address | undefined; label: string | undefined }) => {
  const scanner = useScanner()

  if (!address || !label) {
    return null
  }

  const baseUrl = scanner.url
  const externalAddress = address.toLowerCase()
  const recentFeesUrl = `${baseUrl}token/${externalAddress}`

  return (
    <div className="text-t100">
      <DisplayLabel label={label} />
      <DisplayAddress address={address} />
      <div className="space-x-2 text-xs text-secondary-300">
        <ContractLink baseUrl={baseUrl} externalAddress={externalAddress} />
        <ItemSeparator />
        <DebankLink externalAddress={externalAddress} />
        <ItemSeparator />
        <a href={recentFeesUrl} target="_blank" rel="noopener noreferrer">
          Recent Fees
        </a>
      </div>
    </div>
  )
}

export const BribesAddressInfo = ({ address, label }: { address: Address | undefined; label: string | undefined }) => {
  const scanner = useScanner()

  if (!address || !label) {
    return null
  }

  const baseUrl = scanner.url
  const externalAddress = address.toLowerCase()
  const voterListUrl = `${baseUrl}token/tokenholderchart/${externalAddress}`
  const latestVotesUrl = `${baseUrl}token/${externalAddress}`
  const recentBribesUrl = `${baseUrl}address/${externalAddress}`

  return (
    <div className="text-t100">
      <DisplayLabel label={label} />
      <DisplayAddress address={address} />
      <div className="space-x-2 text-xs text-secondary-300">
        <ContractLink baseUrl={baseUrl} externalAddress={externalAddress} />
        <ItemSeparator />
        <a href={voterListUrl} target="_blank" rel="noopener noreferrer">
          Voter List
        </a>
        <ItemSeparator />
        <a href={latestVotesUrl} target="_blank" rel="noopener noreferrer">
          Latest Votes
        </a>
        <ItemSeparator />
        <a href={recentBribesUrl} target="_blank" rel="noopener noreferrer">
          Recent Bribes
        </a>
      </div>
    </div>
  )
}

const ContractLink = ({ baseUrl, externalAddress }: { baseUrl: string; externalAddress: string }) => {
  const contractUrl = `${baseUrl}address/${externalAddress}#code`
  return (
    <a href={contractUrl} target="_blank" rel="noopener noreferrer">
      Contract
    </a>
  )
}

const DebankLink = ({ externalAddress }: { externalAddress: string }) => {
  const chainConfig = useChainConfig()

  let slug = ""
  switch (chainConfig?.chainId) {
    case 250:
      slug = "ftm"
      break
    case 8453:
      slug = "base"
      break
    case 146:
      slug = "sonic"
      break
  }

  if (!chainConfig || !slug) {
    return null
  }

  const debankUrl = `https://debank.com/protocols/pool/${externalAddress}/${slug}`
  return (
    <a href={debankUrl} target="_blank" rel="noopener noreferrer">
      Debank
    </a>
  )
}

const DexScreenerChartLink = ({ externalAddress }: { externalAddress: string }) => {
  const baseUrl = "https://debank.com/protocols/pool/"
  const chartUrl = `${baseUrl}${externalAddress}/chart`
  return (
    <a href={chartUrl} target="_blank" rel="noopener noreferrer">
      Chart
    </a>
  )
}

const ItemSeparator = () => {
  return <span className="text-t100">|</span>
}

const DisplayLabel = ({ label }: { label: string }) => {
  return <div className="text-sm">{label}</div>
}

const DisplayAddress = ({ address }: { address: Address }) => {
  const { isMobile } = useResponsive()

  let addressText = ""
  if (address) {
    addressText = isMobile ? trimEthAddress(address, 6, 6) : address
  }
  return (
    <div className="flex cursor-pointer items-center justify-start gap-2 text-sm text-t300 hover:text-t200">
      {addressText} <CopyIcon size={12} onClick={() => copyToClipboard(address!)} />
    </div>
  )
}

const copyToClipboard = (text: string) => {
  void navigator.clipboard.writeText(text)
}
